import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import normalizeError from '@rsos/sinatra/src/utils/normalizeError';
import {
  mapLayerSettingsSelector,
  StateWithPsaps,
} from '../capstone/psapInfo/psapInfoSlice';
import { getLoadedGISServices, loadSingleLayer } from './gisServicesAPI';
import {
  type GISService,
  type GISServiceAttributes,
  type GISServicesState,
  type GISStateLayer,
  GISServiceTypes,
  MappedGISIdentity,
} from './gisServicesTypes';
import { reifyLayerMap } from './layer-loaders/buildLayerMap';

export const initialState: GISServicesState = {
  error: undefined,
  gisIdentities: [],
  services: [],
  layers: [],
  onDemandLayers: [],
  loading: false,
};

export const fetchGISServices = createAsyncThunk<
  {
    services: GISService[];
    gisIdentities: MappedGISIdentity[];
    layerMap: GISStateLayer[];
    roleName?: string;
    accountId?: string;
  },
  { orgName: string; shouldLoadLayers?: boolean },
  {
    state: StateWithPsaps & {
      gisServices: GISServicesState;
      sinatra?: {
        user?: {
          currentRole?: {
            name?: string;
            application?: string;
            permissions?: string[];
          };
        };
      };
    };
    rejectValue: { message: string; status?: number };
  }
>(
  'gisServices/fetchGISServices',
  async ({ orgName, shouldLoadLayers }, { rejectWithValue, getState }) => {
    try {
      const settings = mapLayerSettingsSelector(getState());
      const state = getState();
      const roleName = state.sinatra?.user?.currentRole?.name;
      const currentPSAP = state.psaps?.currentPSAP;
      const accountId = currentPSAP?.account_id;

      const result = await getLoadedGISServices(
        orgName,
        settings,
        shouldLoadLayers,
      );

      return {
        ...result,
        roleName,
        accountId,
      };
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue({
        message,
        status: (error as AxiosError).response?.status,
      });
    }
  },
);

export const loadGISLayer = createAsyncThunk<
  { layerId: string; loadedLayer: GISStateLayer },
  { service: GISService },
  {
    state: { gisServices: GISServicesState };
    rejectValue: { message: string; layerId: string };
  }
>(
  'gisServices/loadGISLayer',
  async ({ service }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const roleName = state.gisServices.roleName;
      const loadedLayer = await loadSingleLayer(service);

      return {
        layerId: service.gisLayerID,
        loadedLayer: {
          ...loadedLayer,
          filteredSublayerIds: computeFilteredSublayerIdsForService(
            loadedLayer,
            roleName,
          ),
        },
      };
    } catch (error) {
      return rejectWithValue({
        message:
          error instanceof Error ? error.message : 'Failed to load layer',
        layerId: service.gisLayerID,
      });
    }
  },
);

function computeFilteredSublayerIdsForService(
  layer: GISStateLayer,
  roleName?: string,
): string[] {
  const rolesConfig = layer.service.attributes?.roles_config;

  if (!rolesConfig?.roles || !roleName) {
    return [];
  }

  const filteredIds: string[] = [];

  for (const role of rolesConfig.roles) {
    if (role.name.toLowerCase() === roleName.toLowerCase()) {
      for (const sublayer of layer.sublayers) {
        const isExcluded = role.exclude.some(
          excludeTitle =>
            excludeTitle.toLowerCase() === sublayer.title.toLowerCase(),
        );
        if (isExcluded) {
          filteredIds.push(sublayer.uniqueId);
        }
      }
    }
  }

  return filteredIds;
}

/**
 * Find a layer by id across both the PSAP-bootstrapped `layers`
 * array AND the runtime `onDemandLayers` array. Reducers / thunks
 * use this so a single reducer can act on either source without
 * the call-site needing to care which bucket the layer landed in.
 */
const getLayer = (id: string, state: GISServicesState) => {
  return (
    state.layers.find(l => l.id === id) ??
    state.onDemandLayers.find(l => l.id === id)
  );
};

/**
 * Locate a layer's index inside whichever source array holds it.
 * Returns the array reference plus the index so the caller can
 * splice / replace in place — `loadGISLayer.fulfilled` needs this
 * to swap a placeholder entry for its loaded counterpart without
 * caring about which bucket the placeholder came from.
 */
const findLayerIndex = (
  id: string,
  state: GISServicesState,
): { source: GISStateLayer[]; index: number } | undefined => {
  const inLayers = state.layers.findIndex(l => l.id === id);
  if (inLayers !== -1) return { source: state.layers, index: inLayers };
  const inOnDemand = state.onDemandLayers.findIndex(l => l.id === id);
  if (inOnDemand !== -1) {
    return { source: state.onDemandLayers, index: inOnDemand };
  }
  return undefined;
};

const getSublayer = (uniqueId: string, layer?: GISStateLayer) => {
  // We accept a missing layer so this can chain well with getLayer
  const sublayers = layer?.sublayers || [];
  return sublayers.find(l => l.uniqueId === uniqueId);
};

/**
 * Computes which sublayer uniqueIds should be filtered for the given layer and role.
 * Returns an array of sublayer uniqueIds that should be hidden.
 */
function computeFilteredSublayerIds(
  layer: GISStateLayer,
  roleName?: string,
): string[] {
  const rolesConfig = layer.service.attributes?.roles_config;

  // If no roles_config or no roleName, no filtering needed
  if (!rolesConfig?.roles || !roleName) {
    return [];
  }

  const filteredIds: string[] = [];

  // Find matching role (case-insensitive)
  for (const role of rolesConfig.roles) {
    if (role.name.toLowerCase() === roleName.toLowerCase()) {
      // Check each sublayer against the exclude list
      for (const sublayer of layer.sublayers) {
        const isExcluded = role.exclude.some(
          excludeTitle =>
            excludeTitle.toLowerCase() === sublayer.title.toLowerCase(),
        );
        if (isExcluded) {
          filteredIds.push(sublayer.uniqueId);
        }
      }
    }
  }

  return filteredIds;
}

/**
 * Gets valid sublayers (excluding filtered ones based on layer.filteredSublayerIds)
 */
function getValidSublayers(layer: GISStateLayer): typeof layer.sublayers {
  const filteredIds = layer.filteredSublayerIds || [];
  return layer.sublayers.filter(s => !filteredIds.includes(s.uniqueId));
}

/**
 * Calculates if the service checkbox should be checked based on valid sublayers
 * Service checkbox is checked if AT LEAST ONE valid sublayer is active
 */
function calculateServiceActiveState(layer: GISStateLayer): boolean {
  const validSublayers = getValidSublayers(layer);
  if (validSublayers.length === 0) {
    return false;
  }
  // Service is "active" (checkbox checked) if AT LEAST ONE valid sublayer is active
  return validSublayers.some(s => s.active);
}

/**
 * Computes filteredSublayerIds for each layer based on roles_config and roleName.
 * Sets active: false for any sublayers that should be filtered.
 */
function filterLayersByUserContext(
  layerMap: GISStateLayer[],
  roleName?: string,
): GISStateLayer[] {
  return layerMap.map(layer => {
    const filteredSublayerIds = computeFilteredSublayerIds(layer, roleName);

    // If no sublayers need filtering, return layer as-is (no filteredSublayerIds field)
    if (filteredSublayerIds.length === 0) {
      return layer;
    }

    // Add filteredSublayerIds and set active: false for filtered sublayers
    return {
      ...layer,
      filteredSublayerIds,
      sublayers: layer.sublayers.map(sublayer => {
        if (filteredSublayerIds.includes(sublayer.uniqueId)) {
          return {
            ...sublayer,
            active: false, // Always false for filtered sublayers
          };
        }
        return sublayer;
      }),
    };
  });
}

const gisServicesSlice = createSlice({
  name: 'gisServices',
  initialState,
  reducers: {
    clearAllGISServices: state => {
      const reset = (l: GISStateLayer) => {
        l.active = false;
        l.sublayers.forEach(s => (s.active = false));
      };
      state.layers.forEach(reset);
      // On-demand layers are part of the visible layer set as far
      // as the user is concerned, so a "Clear All" should sweep
      // them too — otherwise the controls panel would say "0
      // selected" while a `// add`-injected layer stayed lit on
      // the map.
      state.onDemandLayers.forEach(reset);
    },
    resetLayerLoadedState: state => {
      state.layers.forEach(l => {
        l.loaded = false;
      });
    },
    updateSublayer: (
      state,
      action: PayloadAction<{
        sublayerID: string;
        layerID: string;
        active: boolean;
      }>,
    ) => {
      const { sublayerID, layerID, active } = action.payload;
      const layer = getLayer(layerID, state);
      const sublayer = getSublayer(sublayerID, layer);
      if (!layer || !sublayer) {
        // eslint-disable-next-line no-console
        console.error(
          'Attempted to update nonexistent layer:',
          layerID,
          sublayerID,
        );
        return;
      }

      // Check if this sublayer is filtered - if so, always keep active: false
      const isFilteredSublayer =
        layer.filteredSublayerIds?.includes(sublayerID) ?? false;

      if (isFilteredSublayer) {
        sublayer.active = false;
        return;
      }

      // Note that the actual GIS toggling of visibility happens in emergency_data/src/hooks/useGISLayersVisibility.ts
      sublayer.active = active;

      // Recalculate service checkbox state based on valid sublayers
      layer.active = calculateServiceActiveState(layer);
    },
    /**
     * Inject an on-demand GIS service + placeholder layer into the
     * dedicated `onDemandLayers` bucket. Used by feature surfaces
     * (today: the `// add` action in `GlobalMapSearch`) that let
     * the user add layers dynamically rather than relying on the
     * PSAP-side gis-services bootstrap.
     *
     * Storage choice: kept under `state.onDemandLayers` rather than
     * mixed into `state.layers` so:
     *   - the array can be redux-persist blacklisted independently
     *     (these layers should never survive a refresh).
     *   - `state.services` (the canonical PSAP-configured catalog)
     *     stays untouched — on-demand layers are not part of the
     *     service catalog, just a transient view layer.
     *
     * Idempotent across BOTH layer arrays: a repeat `// add` of a
     * layer that's already PSAP-bootstrapped (or already injected
     * here) is a no-op so we don't double-render the same layer in
     * the controls panel.
     *
     * Downstream selectors (`selectGISLayers*`) merge this bucket
     * into the regular layers array transparently, so map mounting
     * (`Map.js` watches `selectGISLayers`) and the layer-controls
     * panel (also watches `selectGISLayers`) work without any
     * extra wiring.
     */
    addOnDemandLayer: (
      state,
      action: PayloadAction<{ service: GISService }>,
    ) => {
      const { service } = action.payload;
      const layerId = service.gisLayerID;
      if (state.layers.some(l => l.id === layerId)) return;
      if (state.onDemandLayers.some(l => l.id === layerId)) return;
      state.onDemandLayers.push({
        id: layerId,
        title: service.display_name,
        service,
        active: false,
        sublayers: [],
        loading: false,
        loaded: false,
      });
    },
    removeOnDemandLayer: (
      state,
      action: PayloadAction<{ layerID: string }>,
    ) => {
      const { layerID } = action.payload;
      state.onDemandLayers = state.onDemandLayers.filter(l => l.id !== layerID);
    },
    updateGISServiceAttributes: (
      state,
      action: PayloadAction<{
        layerID: string;
        attributes: Partial<GISServiceAttributes>;
      }>,
    ) => {
      const { layerID, attributes } = action.payload;
      const layer = getLayer(layerID, state);
      if (!layer) return;

      layer.service.attributes = {
        ...(layer.service.attributes || {}),
        ...attributes,
      };
      layer.sublayers.forEach(sublayer => {
        sublayer.service.attributes = {
          ...(sublayer.service.attributes || {}),
          ...attributes,
        };
      });

      const service = state.services.find(s => s.id === layer.service.id);
      if (service) {
        service.attributes = {
          ...(service.attributes || {}),
          ...attributes,
        };
      }
    },
    updateLayer: (
      state,
      action: PayloadAction<{ layerID: string; active: boolean }>,
    ) => {
      const { layerID, active } = action.payload;
      const layer = getLayer(layerID, state) as GISStateLayer;
      if (!layer) {
        // eslint-disable-next-line no-console
        console.error('Attempted to activate nonexistent layer:', layerID);
        return;
      }

      // Service layer is ALWAYS visible on the map
      // The checkbox state (layer.active) only reflects if all valid sublayers are active
      // When toggling the service checkbox, we only toggle the sublayers, not the service visibility

      const filteredIds = layer.filteredSublayerIds || [];
      const validSublayers = getValidSublayers(layer);

      // For layers without sublayers, directly set the active state
      if (validSublayers.length === 0) {
        layer.active = active;
        return;
      }

      // Update all sublayers based on checkbox state
      // Filtered sublayers remain inactive
      layer.sublayers.forEach(s => {
        if (filteredIds.includes(s.uniqueId)) {
          // Always keep filtered sublayer inactive
          s.active = false;
        } else {
          // All other sublayers (valid ones) toggle based on checkbox
          s.active = active;
        }
      });

      // Recalculate service checkbox state based on valid sublayers
      layer.active = calculateServiceActiveState(layer);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchGISServices.pending, state => {
        state.loading = true;
      })
      .addCase(fetchGISServices.fulfilled, (state, action) => {
        state.error = initialState.error;
        state.loading = false;
        state.gisIdentities = action.payload.gisIdentities;
        state.services = action.payload.services;

        const roleName = action.payload.roleName;
        const accountId = action.payload.accountId;

        // Store accountId and roleName in state for use in reducers
        state.accountId = accountId;
        state.roleName = roleName;

        // Compute filteredSublayerIds based on roles_config and roleName
        state.layers = filterLayersByUserContext(
          action.payload.layerMap,
          roleName,
        );
      })
      .addCase(fetchGISServices.rejected, (state, action) => {
        if (action.payload) {
          const { message = '', status = undefined } = action.payload;

          state = {
            ...initialState,
            error: {
              message: message,
              status: status,
            },
          };

          return state;
        }
      })
      .addCase(loadGISLayer.pending, (state, action) => {
        const layerId = action.meta.arg.service.gisLayerID;
        const layer = getLayer(layerId, state);
        if (layer) {
          layer.loading = true;
        }
      })
      .addCase(loadGISLayer.fulfilled, (state, action) => {
        const { layerId, loadedLayer } = action.payload;
        const found = findLayerIndex(layerId, state);
        if (found) {
          // Splice in place against whichever array (`layers` or
          // `onDemandLayers`) the placeholder lives in so we don't
          // accidentally promote an on-demand layer into the PSAP
          // catalog (or vice versa) during the load round-trip.
          found.source[found.index] = {
            ...loadedLayer,
            loading: false,
            loaded: true,
            filteredSublayerIds: loadedLayer.filteredSublayerIds,
          };
        }
      })
      .addCase(loadGISLayer.rejected, (state, action) => {
        if (action.payload) {
          const { layerId } = action.payload;
          const layer = getLayer(layerId, state);
          if (layer) {
            layer.loading = false;
            layer.loaded = false;
          }
        }
      });
  },
});

export const {
  updateLayer,
  updateSublayer,
  clearAllGISServices,
  resetLayerLoadedState,
  addOnDemandLayer,
  removeOnDemandLayer,
  updateGISServiceAttributes,
} = gisServicesSlice.actions;

/**
 * SELECTORS:
 */

// Some components call these selectors in contexts where the slice is not
// loaded. This is not good practice, but it is simpler to handle that case
// here, for now.
const emptyServicesList: GISService[] = [];
export const selectGISServices = (state: { gisServices: GISServicesState }) => {
  return state.gisServices?.services || emptyServicesList;
};

export const selectBasemapService = createSelector(
  [selectGISServices],
  gisServices =>
    gisServices.find(l => l.service_type === GISServiceTypes.basemap),
);

export const selectSupplementalServices = createSelector(
  [selectGISServices],
  gisServices => {
    return gisServices.filter(
      l => l.service_type === GISServiceTypes.supplemental,
    );
  },
);

export const selectGeocodeService = createSelector(
  [selectGISServices],
  gisServices => {
    return gisServices.find(l => l.service_type === GISServiceTypes.geocode);
  },
);

const emptyLayersList: GISStateLayer[] = [];
/**
 * Returns a nested list of layers and sublayers for use in
 * MapLayerControls, etc. Merges PSAP-bootstrapped `layers` with
 * runtime `onDemandLayers` (today: layers added via the
 * `// add` action) so downstream consumers see one unified array
 * — Map.js will mount both, MapLayersControlWithTabs will render
 * checkboxes for both, click-to-identify works against both, etc.
 *
 * Order matters for the controls panel: PSAP-configured layers
 * stay at the top in their admin-defined `display_order`, with
 * on-demand layers appended at the end. That way the user's
 * just-added layer is always immediately visible at the bottom
 * of its category tab rather than scattered through the list.
 */
export const selectGISLayersRaw = createSelector(
  [
    (state: { gisServices: GISServicesState }) =>
      state.gisServices?.layers || emptyLayersList,
    (state: { gisServices: GISServicesState }) =>
      state.gisServices?.onDemandLayers || emptyLayersList,
  ],
  (layers, onDemandLayers) => {
    if (onDemandLayers.length === 0) return layers;
    return [...layers, ...onDemandLayers];
  },
);

/**
 * Returns a nested list of layers and sublayers for use in MapLayerControls, etc.
 */
export const selectGISLayers = createSelector(
  selectGISLayersRaw,
  reifyLayerMap,
);

export const selectNonBasemapLayers = createSelector(
  selectGISLayers,
  layers => {
    return layers.filter(
      l => l.service.service_type != GISServiceTypes.basemap,
    );
  },
);

export const selectActiveLayers = createSelector(selectGISLayers, layers => {
  return layers.filter(l => l.active);
});

export const selectActiveServices = createSelector(
  selectActiveLayers,
  layers => {
    return layers.map(l => l.service);
  },
);

export const selectActiveNonBasemapServices = createSelector(
  selectActiveServices,
  services => {
    return services.filter(s => s.service_type !== GISServiceTypes.basemap);
  },
);

export const selectActiveClickToIdentifyServices = createSelector(
  selectActiveLayers,
  layers => {
    return layers.filter(
      l =>
        l.service.attributes?.is_click_to_identify_service ||
        l.service.service_type == GISServiceTypes.basemap,
    );
  },
);

/**
 * PERSISTENCE
 */

const persistConfig = {
  key: 'gisServices',
  storage,
  whitelist: [], // Do not persist on page refresh
  // Defense-in-depth: if `whitelist` ever gets populated (e.g. to
  // remember user-toggled layers across reloads) `onDemandLayers`
  // MUST stay out — these are by-design transient and outliving the
  // tab would resurrect layers the user thought they'd discarded.
  blacklist: ['onDemandLayers'],
};

export const gisServices = persistReducer(
  persistConfig,
  gisServicesSlice.reducer,
);
